import { useRef, useCallback, useEffect } from "react";

/**
 * Adds click-and-drag (desktop) and touch-drag (mobile) horizontal scrolling
 * to a container. Works with both overflow-x-auto containers and CSS-animated
 * marquees (pauses animation on drag, resumes on release).
 *
 * For CSS-animated marquees, wrap the animated element inside an overflow-hidden
 * container and convert to overflow-x-auto when dragging.
 */
export function useDragScroll<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const hasMoved = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const el = ref.current;
    if (!el || (e.pointerType === "mouse" && e.button !== 0)) return;
    isDragging.current = true;
    hasMoved.current = false;
    startX.current = e.clientX;
    scrollLeft.current = el.scrollLeft;
    el.style.cursor = "grabbing";
    el.style.userSelect = "none";
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    const el = ref.current;
    if (!el) return;
    const dx = e.clientX - startX.current;
    if (Math.abs(dx) > 8) hasMoved.current = true;
    el.scrollLeft = scrollLeft.current - dx;
  }, []);

  const onPointerUp = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    isDragging.current = false;
    el.style.cursor = "grab";
    el.style.userSelect = "";
  }, []);

  const onPointerLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    isDragging.current = false;
    el.style.cursor = "grab";
    el.style.userSelect = "";
  }, []);

  // Prevent the click immediately following a real drag, but allow normal taps/clicks.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const preventClick = (e: MouseEvent) => {
      if (!hasMoved.current) return;
      e.preventDefault();
      e.stopPropagation();
      hasMoved.current = false;
    };

    el.addEventListener("click", preventClick, true);
    return () => el.removeEventListener("click", preventClick, true);
  }, []);

  return {
    ref,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerLeave,
      onPointerLeave,
    },
    style: { cursor: "grab" } as React.CSSProperties,
  };
}
